// src/components/dashboards/StudentThesisProposalSection.jsx
import React, { useState, useEffect } from 'react';
import { studentThesisAPI, studentEthicsAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

const ACCENT = 'rgb(49, 154, 185)';
const ACCENT_L = 'rgba(49, 154, 185, 0.12)';

const labelStyle = {
  fontSize: '11px',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  marginBottom: '6px',
  display: 'block',
};

const inputStyle = {
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid #cbd5e1',
  fontSize: '14px',
  outline: 'none',
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
  background: '#fff',
};

const btnStyle = {
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  background: ACCENT,
  color: '#fff',
  fontWeight: 700,
  fontSize: '13px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background .15s',
};

const btnGhostStyle = {
  padding: '10px 20px',
  borderRadius: '8px',
  border: `1px solid ${ACCENT}`,
  background: '#fff',
  color: ACCENT,
  fontWeight: 700,
  fontSize: '13px',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const getFileIcon = (fileName) => {
  if (!fileName) return '📎';
  if (fileName.endsWith('.pdf')) return '📕';
  if (/\.(jpg|jpeg|png|gif|webp)/i.test(fileName)) return '🖼';
  return '📄';
};

const getStatusDetails = (status) => {
  switch (status) {
    case 'DRAFT':
      return { title: 'Draft Thesis', label: 'Draft', color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', desc: 'Please upload your draft thesis document and submit for Faculty Review.' };
    case 'SUBMITTED':
      return { title: 'Pending Faculty Review', label: 'Faculty Reviewing', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', desc: 'Your draft thesis is currently under review by the Community Medicine Faculty.' };
    case 'CHANGES_REQUESTED':
      return { title: 'Changes Requested', label: 'Changes Requested', color: '#ea580c', bg: '#fff7ed', border: '#ffedd5', desc: 'Faculty has requested modifications to your draft thesis.' };
    case 'DRAFT_APPROVED':
      return { title: 'Draft Approved', label: 'Draft Approved', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', desc: 'Your draft thesis has been approved! Please upload your final dissertation PDF and submit to the COE.' };
    case 'FACULTY_APPROVED':
      return { title: 'Faculty Approved', label: 'Approved by Faculty', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', desc: 'Your draft thesis has been approved! Please upload your final dissertation PDF and submit to the COE.' };
    case 'FINAL_SUBMITTED':
      return { title: 'Final Submitted', label: 'Final Submitted', color: '#0d9488', bg: '#f0fdfa', border: '#ccfbf1', desc: 'Your final dissertation is submitted to the COE. Awaiting evaluation.' };
    case 'READY_FOR_COE_EVALUATION':
      return { title: 'Ready for COE Evaluation', label: 'Ready for COE', color: '#0d9488', bg: '#f0fdfa', border: '#ccfbf1', desc: 'Your final thesis is ready for COE evaluation.' };
    case 'UNDER_COE_EVALUATION':
      return { title: 'Under COE Evaluation', label: 'COE Evaluating', color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff', desc: 'The COE is currently evaluating your final dissertation.' };
    case 'EVALUATED':
      return { title: 'Evaluated', label: 'Evaluated by COE', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', desc: 'Your dissertation has been evaluated. Marks will be published shortly.' };
    case 'MARKS_PUBLISHED':
      return { title: 'Marks Published', label: 'Marks Published', color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', desc: 'Evaluation marks and remarks have been published.' };
    case 'REJECTED':
      return { title: 'Rejected', label: 'Rejected', color: '#dc2626', bg: '#fef2f2', border: '#fee2e2', desc: 'Your thesis submission was rejected by the faculty.' };
    case 'UNDER_REVIEW':
      return { title: 'Under Review', label: 'Under Review', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', desc: 'Your thesis is currently under review.' };
    default:
      return { title: 'Unknown', label: 'Unknown', color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', desc: '' };
  }
};

const StudentThesisProposalSection = () => {
  const [thesis, setThesis] = useState(null);
  const [approvedEthics, setApprovedEthics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Form Fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedEthicsId, setSelectedEthicsId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [thesisData, ethicsData] = await Promise.all([
        studentThesisAPI.getMyThesis(),
        studentEthicsAPI.getSubmissions()
      ]);

      setThesis(thesisData || null);

      // Filter only APPROVED ethics clearance records of type THESIS_STUDY
      const approved = ethicsData.filter(
        item => item.status === 'APPROVED' && item.study_type === 'THESIS_STUDY'
      );
      setApprovedEthics(approved);

      if (thesisData) {
        setTitle(thesisData.title || '');
        setDescription(thesisData.description || '');
        setSelectedEthicsId(thesisData.ethics_study || '');
      } else if (approved.length > 0) {
        setSelectedEthicsId(approved[0].id);
      }
    } catch (err) {
      console.error('Failed to load student thesis or ethics details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
    return `${backendBase}${url}`;
  };

  const handleCreateThesis = async (e) => {
    e.preventDefault();
    if (!title.trim() || !selectedEthicsId) {
      notify.success('Thesis Title and approved Ethics study link are required.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await studentThesisAPI.createThesis({
        title: title.trim(),
        description: description.trim(),
        ethics_study: selectedEthicsId,
      });
      setThesis(data);
      setIsEditing(false);
      notify.success('Thesis Submission record created successfully!');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to create thesis submission.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateThesis = async (e) => {
    e.preventDefault();
    if (!title.trim() || !selectedEthicsId) {
      notify.success('Thesis Title and approved Ethics study link are required.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await studentThesisAPI.patchThesis({
        title: title.trim(),
        description: description.trim(),
        ethics_study: selectedEthicsId,
      });
      setThesis(data);
      setIsEditing(false);
      notify.success('Thesis Details updated successfully!');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to update thesis details.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusTransition = async (newStatus) => {
    let confirmMsg = '';
    if (newStatus === 'SUBMITTED') {
      confirmMsg = 'Are you sure you want to submit your draft thesis to the faculty for review?';
    } else if (newStatus === 'FINAL_SUBMITTED') {
      confirmMsg = 'Are you sure you want to submit your final dissertation to the COE? You cannot edit or upload files after this.';
    }

    if (confirmMsg && !await confirmDialog(confirmMsg)) return;

    setSubmitting(true);
    try {
      const data = await studentThesisAPI.patchThesis({ status: newStatus });
      setThesis(data);
      notify.success(`Thesis status updated to: ${getStatusDetails(newStatus).title}`);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to update status.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const data = await studentThesisAPI.uploadDocument(thesis.id, formData);
      setThesis(data);
      notify.success('Document uploaded successfully!');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to upload document.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!await confirmDialog('Are you sure you want to delete this document?')) return;

    try {
      const data = await studentThesisAPI.deleteDocument(thesis.id, docId);
      setThesis(data);
      notify.success('Document deleted successfully.');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to delete document.');
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading Thesis Submission details…</div>;
  }

  // Case 1: Student has no thesis created yet, and no approved Ethics record
  if (!thesis && approvedEthics.length === 0) {
    return (
      <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '30px', textAlign: 'center' }}>
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>⚠️</span>
        <h3 style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
          Ethics Clearance Required
        </h3>
        <p style={{ margin: '0 auto 20px', fontSize: '14px', color: '#64748b', maxWidth: '500px', lineHeight: 1.6 }}>
          You must have at least one approved Ethics Clearance record of type "Thesis Study" before you can submit a thesis.
          Please visit the <strong>Ethics Clearance</strong> tab to submit a proposal.
        </p>
      </div>
    );
  }

  // Case 2: Create or Edit Form
  if (!thesis || isEditing) {
    return (
      <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
          {thesis ? 'Edit Thesis Submission details' : 'New Thesis Submission'}
        </h3>
        <form onSubmit={thesis ? handleUpdateThesis : handleCreateThesis} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Linked Approved Ethics Study *</label>
            <select
              style={inputStyle}
              value={selectedEthicsId}
              onChange={e => setSelectedEthicsId(e.target.value)}
              required
            >
              <option value="">-- Select Approved Ethics Record --</option>
              {approvedEthics.map(item => (
                <option key={item.id} value={item.id}>
                  {item.project_title} (Approved on {new Date(item.submitted_at).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Thesis Title *</label>
            <input 
              style={inputStyle} 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              placeholder="e.g. Clinical Study on Pediatric Cardiac Arrest Management" 
              required 
            />
          </div>

          <div>
            <label style={labelStyle}>Abstract / Description</label>
            <textarea 
              style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }} 
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              placeholder="Provide clinical details, objectives, or overall abstract..." 
            />
          </div>

          {/* Guide display (derived from selected ethics) */}
          {selectedEthicsId && (
            <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span style={labelStyle}>Assigned Guide (Derived from Ethics Clearance)</span>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                {approvedEthics.find(item => String(item.id) === String(selectedEthicsId))?.guide_name_display || 'N/A'}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
            {thesis && (
              <button 
                type="button" 
                onClick={() => {
                  setIsEditing(false);
                  setTitle(thesis.title || '');
                  setDescription(thesis.description || '');
                  setSelectedEthicsId(thesis.ethics_study || '');
                }} 
                style={{ ...btnGhostStyle, borderColor: '#cbd5e1', color: '#64748b' }}
              >
                Cancel
              </button>
            )}
            <button type="submit" disabled={submitting} style={btnStyle}>
              {submitting ? 'Saving...' : '💾 Save Thesis Details'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Case 3: View Submission Details
  const statusDetails = getStatusDetails(thesis.status);
  const draftDocs = thesis.documents?.filter(d => d.document_type === 'DRAFT') || [];
  const finalDocs = thesis.documents?.filter(d => d.document_type === 'FINAL') || [];
  
  const canEdit = ['DRAFT', 'CHANGES_REQUESTED', 'REJECTED'].includes(thesis.status);
  const isFacultyReviewPending = thesis.status === 'SUBMITTED';

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 20 }}>
      
      {/* Alert Header showing status details */}
      <div style={{ 
        background: statusDetails.bg, 
        border: `1px solid ${statusDetails.border}`, 
        borderRadius: 12, 
        padding: '14px 18px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: 12 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24 }}>💡</span>
          <div>
            <div style={{ fontWeight: 700, color: statusDetails.color, fontSize: 14 }}>
              Status: {statusDetails.title}
            </div>
            <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>
              {statusDetails.desc}
            </div>
          </div>
        </div>
        {canEdit && (
          <button onClick={() => setIsEditing(true)} style={btnGhostStyle}>
            ✏️ Edit Details
          </button>
        )}
      </div>

      {/* Main Grid: Info and Documents */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20 }}>
        
        {/* Left Column: Detailed Metadata */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
              Thesis Information
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <span style={labelStyle}>Thesis Title</span>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{thesis.title}</div>
            </div>

            <div>
              <span style={labelStyle}>Linked Ethics Study</span>
              <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>
                {thesis.ethics_study_title || 'N/A'}
              </div>
            </div>

            {thesis.description && (
              <div>
                <span style={labelStyle}>Abstract / Description</span>
                <div style={{ fontSize: 13, color: '#475569', background: '#f8fafc', padding: '12px', borderRadius: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {thesis.description}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', borderTop: '1px solid #f1f5f9', paddingTop: '14px' }}>
              <div>
                <span style={labelStyle}>Guide / Faculty Reviewer</span>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{thesis.guide_name || 'N/A'}</div>
              </div>
              <div>
                <span style={labelStyle}>Student Registration</span>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{thesis.student_id_num || 'N/A'}</div>
              </div>
            </div>

            {/* Date timeline details */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', fontSize: '11px', color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
              <div>
                <strong>Draft Submitted:</strong>
                <div>{thesis.draft_submitted_at ? new Date(thesis.draft_submitted_at).toLocaleDateString('en-IN') : 'Not submitted'}</div>
              </div>
              <div>
                <strong>Faculty Approved:</strong>
                <div>
                  {thesis.faculty_approved_at 
                    ? `${new Date(thesis.faculty_approved_at).toLocaleDateString('en-IN')}${thesis.status === 'DRAFT_APPROVED' ? ' (Draft)' : ''}`
                    : (['DRAFT_APPROVED', 'FACULTY_APPROVED', 'READY_FOR_COE_EVALUATION', 'FINAL_SUBMITTED', 'UNDER_COE_EVALUATION', 'EVALUATED', 'MARKS_PUBLISHED'].includes(thesis.status) 
                        ? (thesis.status === 'DRAFT_APPROVED' ? 'Approved (Draft)' : 'Approved') 
                        : 'Not approved')}
                </div>
              </div>
              <div>
                <strong>Final Submitted:</strong>
                <div>{thesis.final_submitted_at ? new Date(thesis.final_submitted_at).toLocaleDateString('en-IN') : 'Not submitted'}</div>
              </div>
            </div>

            {/* Faculty remarks card */}
            {thesis.faculty_remarks && (
              <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '12px' }}>
                <div style={{ fontWeight: 700, color: '#b45309', fontSize: 12 }}>Faculty Remarks:</div>
                <div style={{ fontSize: 12, color: '#78350f', marginTop: 4 }}>
                  {thesis.faculty_remarks}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Documents Upload & Marks */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          {/* Document Management section */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
              📎 Thesis Documents
            </div>

            {/* Draft Documents list */}
            <div>
              <span style={labelStyle}>Draft Versions</span>
              {draftDocs.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#94a3b8', padding: '6px 0' }}>No draft files uploaded yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {draftDocs.map(doc => (
                    <div key={doc.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span style={{ fontSize: 16 }}>{getFileIcon(doc.file_name)}</span>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                          {doc.file_name}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <a href={getFullUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: ACCENT, fontSize: 11, fontWeight: 700 }}>View</a>
                        {canEdit && (
                          <button onClick={() => handleDeleteDoc(doc.id)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Final Dissertation list */}
            {['FACULTY_APPROVED', 'DRAFT_APPROVED', 'FINAL_SUBMITTED', 'READY_FOR_COE_EVALUATION', 'UNDER_COE_EVALUATION', 'EVALUATED', 'MARKS_PUBLISHED'].includes(thesis.status) && (
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                <span style={labelStyle}>Final Dissertation</span>
                {finalDocs.length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#94a3b8', padding: '6px 0' }}>No final dissertation uploaded yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {finalDocs.map(doc => (
                      <div key={doc.id} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                          <span style={{ fontSize: 16 }}>📕</span>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                            {doc.file_name}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <a href={getFullUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#166534', fontSize: 11, fontWeight: 700 }}>View</a>
                          {['FACULTY_APPROVED', 'DRAFT_APPROVED'].includes(thesis.status) && (
                            <button onClick={() => handleDeleteDoc(doc.id)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons based on current status */}
            {canEdit && (
              <div>
                <label style={{ display: 'block', cursor: 'pointer', marginTop: 10 }}>
                  <input type="file" style={{ display: 'none' }} accept=".pdf" onChange={handleUploadDoc} disabled={uploadingDoc} />
                  <div style={{ ...btnGhostStyle, textAlign: 'center', borderStyle: 'dashed', background: '#f8fafc' }}>
                    {uploadingDoc ? 'Uploading Draft...' : '➕ Upload Draft PDF'}
                  </div>
                </label>
                {draftDocs.length > 0 && (
                  <button 
                    onClick={() => handleStatusTransition('SUBMITTED')}
                    style={{ ...btnStyle, width: '100%', marginTop: '12px' }}
                    disabled={submitting}
                  >
                    🚀 Submit Draft to Faculty
                  </button>
                )}
              </div>
            )}

            {['FACULTY_APPROVED', 'DRAFT_APPROVED'].includes(thesis.status) && (
              <div>
                {finalDocs.length === 0 && (
                  <label style={{ display: 'block', cursor: 'pointer', marginTop: 10 }}>
                    <input type="file" style={{ display: 'none' }} accept=".pdf" onChange={handleUploadDoc} disabled={uploadingDoc} />
                    <div style={{ ...btnGhostStyle, textAlign: 'center', borderStyle: 'dashed', background: '#f0fdf4', color: '#16a34a', borderColor: '#16a34a' }}>
                      {uploadingDoc ? 'Uploading Dissertation...' : '➕ Upload Final Dissertation PDF'}
                    </div>
                  </label>
                )}
                {finalDocs.length > 0 && (
                  <button 
                    onClick={() => handleStatusTransition('FINAL_SUBMITTED')}
                    style={{ ...btnStyle, width: '100%', marginTop: '12px', background: '#0d9488' }}
                    disabled={submitting}
                  >
                    🚀 Submit Final to COE
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Grading / COE Evaluation Panel */}
          {['FINAL_SUBMITTED', 'READY_FOR_COE_EVALUATION', 'UNDER_COE_EVALUATION', 'EVALUATED', 'MARKS_PUBLISHED'].includes(thesis.status) && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                📊 COE Evaluation Results
              </div>

              {thesis.marks != null ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>Score:</span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: thesis.status === 'MARKS_PUBLISHED' ? '#16a34a' : '#ea580c' }}>
                      {thesis.marks} marks
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>Status:</span>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 700,
                      background: getStatusDetails(thesis.status).bg,
                      color: getStatusDetails(thesis.status).color,
                      border: `1px solid ${getStatusDetails(thesis.status).border}`
                    }}>
                      {getStatusDetails(thesis.status).label}
                    </span>
                  </div>
                  {thesis.remarks && (
                    <div>
                      <div style={labelStyle}>COE Evaluation Remarks</div>
                      <div style={{ fontSize: 12, color: '#374151', background: '#f8fafc', padding: '10px', borderRadius: 6, border: '1px solid #e2e8f0', lineHeight: 1.5 }}>
                        {thesis.remarks}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#64748b', textAlign: 'center', padding: '10px 0' }}>
                  ⏳ Dissertation evaluation is in progress. Marks will be visible here once published.
                </div>
              )}
            </div>
          )}

        </div>
      </div>

    </div>
  );
};

export default StudentThesisProposalSection;
